/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useMemo } from 'react';
import { components, services, unstable } from '@broadleaf/admin-components';
import cx from 'classnames';
import { Form, Formik } from 'formik';
import { filter, flatten, get, isEmpty, merge, noop } from 'lodash';

import { HeaderBar } from '@broadleaf/admin-components/dist/common/components/HeaderBar';
import { ScrollView } from '@broadleaf/admin-components/dist/common/components/ScrollView';
import { PayPalFormProps } from 'app/paypal/components/PayPalView/constants';

import messages from './PayPalForm.messages';

const { FormComponents } = components;
const { SchemaService } = services;
const { MetadataBoundary, useFormatMessage, MetadataUtils, metadataInvariant } =
  unstable;
const { getAttribute, hasEndpointWithType, isGroupComponent } = MetadataUtils;

/**
 * Form components to be rendered for PayPal account management.
 */
const PayPalForm = props => {
  const {
    boundaryClassName,
    className,
    components,
    dispatch,
    dismissed,
    metadata,
    state
  } = props;
  useMetadataValidation(metadata);

  const formComponents = useFormComponents(metadata.components);
  const [primaryComponents, secondaryComponents] = formComponents;
  const isCreate = !get(state, 'data.value', false);
  const formikProps = useFormikProps({
    ...props,
    formComponents,
    isCreate
  });

  return (
    <Formik {...formikProps}>
      {formik => {
        return (
          <ScrollView
            className={cx('tw-h-full tw-bg-gray-200', {
              [className]: !!className
            })}
            component={Form}
          >
            <ScrollView.Header className="tw-w-full" component={HeaderBar}>
              <HeaderBar.Title>{metadata.label}</HeaderBar.Title>
              <HeaderBar.Element grow />
              <components.HeaderActionComponents
                components={components}
                dismissed={dismissed}
                formDispatch={dispatch}
                formState={state}
                metadata={metadata}
              />
            </ScrollView.Header>
            <ScrollView.Content
              className="tw-mx-auto tw-w-full md:tw-p-4"
              component="section"
            >
              <MetadataBoundary
                className={boundaryClassName}
                color="secondary"
                metadata={metadata}
              >
                <div className="tw-flex-no-wrap tw-relative tw-flex tw-flex-col tw-bg-white tw-py-4 tw-shadow lg:tw-flex-row">
                  <div className="tw-flex-grow tw-px-4">
                    <FormComponents
                      components={primaryComponents}
                      formik={formik}
                      readOnly={true}
                    />
                  </div>
                  {!isEmpty(secondaryComponents) && (
                    <div
                      className="tw-flex-none tw-px-4 lg:tw-w-1/3"
                      style={{ minWidth: '348px' }}
                    >
                      <FormComponents
                        components={secondaryComponents}
                        formik={formik}
                        readOnly={true}
                      />
                    </div>
                  )}
                </div>
              </MetadataBoundary>
            </ScrollView.Content>
          </ScrollView>
        );
      }}
    </Formik>
  );
};

PayPalForm.propTypes = PayPalFormProps;

function useMetadataValidation(metadata) {
  useEffect(() => {
    metadataInvariant(
      hasEndpointWithType(metadata, 'READ_PROPERTY'),
      'Must configure an endpoint with type of "READ_PROPERTY" for the "PAYPAL" view type.'
    );
    metadataInvariant(
      hasEndpointWithType(metadata, 'PROVISION_ACCOUNT'),
      'Must configure an endpoint with type of "PROVISION_ACCOUNT" for the "PAYPAL" view type.'
    );
    metadataInvariant(
      hasEndpointWithType(metadata, 'CREATE_ACCOUNT'),
      'Must configure an endpoint with type of "CREATE_ACCOUNT" for the "PAYPAL" view type.'
    );
    metadataInvariant(
      hasEndpointWithType(metadata, 'FINISH_ACCOUNT'),
      'Must configure an endpoint with type of "FINISH_ACCOUNT" for the "PAYPAL" view type.'
    );
    metadataInvariant(
      hasEndpointWithType(metadata, 'UPDATE_ACCOUNT'),
      'Must configure an endpoint with type of "UPDATE_ACCOUNT" for the "PAYPAL" view type.'
    );
    metadataInvariant(
      hasEndpointWithType(metadata, 'READ_ACCOUNT'),
      'Must configure an endpoint with type of "READ_ACCOUNT" for the "PAYPAL" view type.'
    );
  }, [metadata]);
}

/**
 * Used to gather the props for the Formik component. This encapsulates evaluating
 * the initialValues, client-side validation, and the submit handling.
 *
 * @return {Object}
 */
function useFormikProps(props) {
  const { formComponents, state } = props;
  // we build a validation schema for the form components
  const schema = SchemaService.generateSchema(flatten(formComponents));

  // generate the set of default/initial values by casting the schema against an empty object
  const initialValues = useMemo(() => {
    return state.data ? SchemaService.castSchema(state.data, schema) : {};
  }, [schema, state.data]);

  return {
    enableReinitialize: true,
    initialValues,
    // we'll never actually do submissions of the form the normal way, just need
    // to read the accountId property into state
    onSubmit: noop,
    validate: SchemaService.getValidator(schema),
    validateOnChange: true
  };
}

/**
 * Merges the configured components with the default ones and returns them split
 * into primary and secondary groupings.
 *
 * @param {Array} configuredComponents - the set of components defined in the metadata
 * @return {[[{}]]} The components to render split into primary then secondary components
 */
const useFormComponents = configuredComponents => {
  const defaultComponents = useDefaultFormComponents();
  return useMemo(() => {
    const components = merge({}, defaultComponents, configuredComponents);
    const primaryComponents = filter(components, isPrimaryComponent);
    const secondaryComponent = filter(components, isSecondaryComponent);
    return [primaryComponents, secondaryComponent];
  }, [configuredComponents, defaultComponents]);
};

function isPrimaryComponent(component) {
  return (
    !isGroupComponent(component) ||
    getAttribute(component, 'secondary') !== true
  );
}

function isSecondaryComponent(component) {
  return (
    isGroupComponent(component) && getAttribute(component, 'secondary') === true
  );
}

function useDefaultFormComponents() {
  const formatMessage = useFormatMessage();
  return useMemo(
    () => [
      {
        classifier: 'Field',
        hidden: true,
        name: 'id',
        readOnly: true,
        type: 'STRING'
      },
      {
        classifier: 'Field',
        hidden: true,
        name: 'key',
        readOnly: true,
        type: 'STRING'
      },
      {
        classifier: 'Field',
        label: formatMessage(messages.accountIdLabel),
        name: 'value',
        order: 1000,
        readOnly: true,
        type: 'STRING'
      }
    ],
    [formatMessage]
  );
}

export default PayPalForm;
export { PayPalForm };
